import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useState } from "react";

/**
 * A React hook for managing the URL hash as if it were a search query
 *
 * This hook:
 * - Prevents `window is undefined` errors by ensuring it only runs on the client.
 * - Parses the hash string into a key-value map (`hashMap`).
 * - Allows updating or removing specific hash parameters.
 * - Prevents unwanted scrolling when clearing the hash.
 *
 * @param {Object} options - Optional config
 * @param {(args: { hash: string, hashMap: Map<string, string> }) => void} options.onChange
 *        Callback fired when the hash changes.
 */
type UseHashOptions = {
  onChange?: (args: { hash: string; hashMap: Map<string, string> }) => void;
};

const createHashMap = (hash: string) => {
  const newHashMap = new Map<string, string>();
  if (hash.startsWith("#")) {
    const searchParams = new URLSearchParams(hash.slice(1));
    searchParams.forEach((value, key) => newHashMap.set(key, value));
  }
  return newHashMap;
};

export function useHash({ onChange }: UseHashOptions = {}) {
  const isClient = typeof window !== "undefined";
  const [hash, setHash] = useState<string>(
    isClient ? window.location.hash : "",
  );
  const [hashMap, setHashMap] = useState<Map<string, string>>(
    createHashMap(hash),
  );

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const handleHashChange = () => {
      const newHash = window.location.hash;
      setHash(newHash);
      const newHashMap = createHashMap(newHash);
      setHashMap(newHashMap);
      onChange?.({ hash: newHash, hashMap: newHashMap });
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [isClient, onChange]);

  /**
   * Updates the URL hash with the given parameters. Prevents page from scrolling unless a scroll target is provided.
   *
   * @param {string | Record<string, string | null | undefined>} newHashOrParams - The new hash or parameters to update.
   * @param {Element | null} [scrollTarget] - An optional element to smooth scroll to.
   */
  const updateHash = useCallback(
    (
      newHashOrParams: string | Record<string, string | null | undefined>,
      scrollTarget?: Element | null,
    ) => {
      if (!isClient) {
        return;
      }

      const currentScrollY = window.scrollY;

      let newHash = "";
      if (isEmpty(newHashOrParams)) {
        newHash = "";
      } else if (typeof newHashOrParams === "string") {
        newHash = newHashOrParams.startsWith("#")
          ? newHashOrParams
          : `#${newHashOrParams}`;
      } else {
        const searchParams = new URLSearchParams(window.location.hash.slice(1));
        Object.entries(newHashOrParams).forEach(([key, value]) => {
          if (value == null) {
            searchParams.delete(key);
          } else {
            searchParams.set(key, value);
          }
        });

        newHash = searchParams.toString();
      }

      window.location.hash = newHash;

      requestAnimationFrame(() => {
        // Prevent jump by setting scroll to the previous position
        window.scrollTo(0, currentScrollY);

        // Then scroll to the target element if provided
        if (scrollTarget) {
          scrollTarget.scrollIntoView({ behavior: "smooth" });
        }
      });
    },
    [isClient],
  );

  return { hash, hashMap, updateHash };
}
